import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Box, TextField } from "@mui/material";
import { useTranslation, withTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { format } from "date-fns";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";

import SplitterContainer from "@plannel/components/SplitterContainer";
import salesTxnService from "@plannel/services/data/sales-txn-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import dateUtils from "@plannel/utils/date-util";
import {cloneDeep, isEmpty, isEqual, sortBy, split} from "lodash";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ToggleButton from "@plannel/components/ToggleButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar";
import { FS } from "@plannel/utils/font-util";

const { hrchyNum, getCurrencySymbol } = codeUtil;
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const initPeriod = { start: START_DATE, end: END_DATE };
const { snackBarMessageColor: { dataEmpty, networkError } } = ColorUtil;

const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    if (type) {
      if (type === "start") {
        e.setDate(1);
        e.setHours(0);
        e.setMinutes(0);
        e.setSeconds(0);
      } else if (type === "end") {
        e.setMonth(e.getMonth() + 1);
        e.setDate(0);
        e.setHours(23);
        e.setMinutes(59);
        e.setSeconds(59);
      }

      setPeriod({ ...period, [type]: e });
    }
  }
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t("periodStart")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        maxDate={period.end ? period.end : END_DATE}
        minDate={START_MIN_DATE}
        value={period.start}
        openTo={"month"}
        onAccept={onChange}
        onMonthChange={(e) => onChange(e, "start")}
        onYearChange={(e) => onChange(e, "start")}
        onClose={onChange}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 150 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        maxDate={END_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        value={period.end}
        openTo={"month"}
        onAccept={onChange}
        onMonthChange={(e) => onChange(e, "end")}
        onYearChange={(e) => onChange(e, "end")}
        onClose={onChange}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 150 } } } }}
      />
    </LocalizationProvider>
  )
}

const DpTop25LevelItem = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const userData = JSON.parse(localStorage.getItem("user"));

  const gridRef = useRef(null);
  const [isLoad, setIsLoad] = useState(null);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [defaultValue, setDefaultValue] = useState(reduxViewState?.defaultValue ?? null);
  const [defaultCustomerHrchy, setDefaultCustomerHrchy] = useState(reduxViewState?.defaultCustomerHrchy ?? null);

  const { AMT } = codeUtil.toggleType;
  const [toggleType, setToggleType] = useState(reduxViewState?.toggle ?? AMT);
  const [allDatas, setAllDatas] = useState(reduxViewState?.allDatas ?? []);

  const [columnDefs, setColumnDefs] = useState([]);
  const [itemNm, setItemNm] = useState([]);
  const [itemRange, setItemRange] = useState(reduxViewState?.itemRange ?? 10);
  // 번역 전 구성. groupName 을 조회 시점에 t() 로 굳히면 언어를 바꿔도 옛 언어가 남는다.
  const [chartSeriesSpec, setChartSeriesSpec] = useState({ itemNames: [], isAmt: true });
  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);

  /* 품목명은 기준정보 값이라 번역 대상이 아니고, 축 그룹명만 렌더 시점에 t() 로 만든다. */
  const chartSeries = useMemo(() => chartSeriesSpec.itemNames.map(name => ({
    field: name,
    text: name,
    type: "bar",
    orientation: "left",
    groupName: chartSeriesSpec.isAmt ? t("amt") : t("qty"),
    typeOptions: { stackId: "tortpItem" }
  })), [chartSeriesSpec, t]);

  const currency = useRef(userData?.currencyCd ?? "");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [period, setPeriod] = useState(reduxViewState?.period ? { start: new ZDate(reduxViewState?.period?.start), end: new ZDate(reduxViewState?.period?.end) } : initPeriod);
  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ viewName, title });
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, [])

  const defaultColumnDefs = [
    { headerName: "No.", colId: "seqNum", valueGetter: "node.rowIndex + 1", width: 70, pinned: "left", lockPinned: true }, // Index column
    {
      headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"],
      width: 150,
      pinned: "left", lockPinned: true,
    },
    {
      headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"],
      width: 200,
      pinned: "left", lockPinned: true,
    },
  ]

  const settingChartData = (pivotResultdata, isAmt) => {
    const { localDateHeader, data } = pivotResultdata;
    const chartSeriesList = [];
    const chartArry = [];
    const salesPivotData = isAmt ? "salesAmtPivotData" : "salesQtyPivotData";
    const salesTotalData = isAmt ? "amtTotal" : "qtyTotal";

    const sortyByData = sortBy(data, salesTotalData).reverse();

    const dateFormat = [];
    if (!isEmpty(localDateHeader) && !isEmpty(data)) {
      let count = 0;
      for (const resultData of sortyByData) {
        if (count >= itemRange) {
          break;
        }
        const itemNm = resultData.itemNm;

        for (let i = 0, len = localDateHeader.length; i < len; i++) {
          const header = localDateHeader[i];
          const chartKey = dateUtils.formatDate(header, "yyyy-MM");
          dateFormat.push(chartKey);
          resultData[chartKey] = resultData?.[salesPivotData]?.[i] ?? 0;
        }
        const symbol = currency.current && getCurrencySymbol(currency.current) ?
        getCurrencySymbol(currency.current) : userData?.currencySymbol ?? "";

        const copyData = cloneDeep(resultData);
        copyData.currency = symbol;
        
        delete copyData.salesAmtPivotData;
        delete copyData.salesQtyPivotData;
        delete copyData.itemCd;
        delete copyData.amtTotal;
        delete copyData.qtyTotal;

        chartSeriesList.push(itemNm);
        chartArry.push(copyData);
        count++;
      }
    }
    setChartLabel(dateFormat);
    setChartSeriesSpec({ itemNames: chartSeriesList, isAmt });
    setChartData(chartArry);
  };

  const getRequestParams = () => {
    const params = {};
    params.startDate = period.start ?? reduxViewState?.period.start;
    params.endDate = period.end ?? reduxViewState?.period.end;

    params.custHrchyCd = filterValue.customerHrchy?.hrchyCd ?? defaultCustomerHrchy?.hrchyCd;
    params.itemHrchyCd = filterValue.category?.hrchyCd ?? defaultValue?.hrchyCd;
    return params;
  }

  const headerFormatter = (value) => {
    return format(new ZDate(value), "yyyy-MM");
  }

  const addSymbolAmtMeasure = (param) => {
    const symbol = currency.current && getCurrencySymbol(currency.current) ?
    getCurrencySymbol(currency.current) : userData?.currencySymbol ?? "";
    if (!param.value || !param.valueFormatted) {
      return '';
    }
    let formatedValue = param.valueFormatted;
    if (formatedValue?.toString().includes(".")) {
      formatedValue = split(param.valueFormatted, ".")[0];
    }
    return symbol + formatedValue;
  };

  const setGridHeaders = (headers, isAmt) => {
    if (headers.length <= 0) {
      return;
    }

    const colDefs = cloneDeep(defaultColumnDefs);
    colDefs.push(
      {
        headerName: "total", field: "total",
        type: isAmt ? ["nonEditableColumn", "decimalPointColumn", "rightAligned"]
                    : ["nonEditableColumn", "numberColumn", "rightAligned"],
        width: 120,
        pinned: "left", lockPinned: true,
        cellRenderer: isAmt ? addSymbolAmtMeasure : param => param.valueFormatted,
      }
    )

    headers.forEach((header) => {
      colDefs.push(
        { headerName: header, 
          field: header,
          colId: `${GridUtils.IGNORE_P13N_COLLID}_${header}`,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          width: 100,
          cellRenderer: isAmt ? addSymbolAmtMeasure : param => param.valueFormatted, 
        });
    });

    setColumnDefs(colDefs);
  }

  const setGridDatas = (headers, results, isAmt) => {
    const datas = [];
    const salesPivotData = isAmt ? "salesAmtPivotData" : "salesQtyPivotData";
    const salesTotalData = isAmt ? "amtTotal" : "qtyTotal";
    const sortyByData = sortBy(results, salesTotalData).reverse();
    let count = 0;
    for (const resultsData of sortyByData) {
      if (count >= itemRange) {
        break;
      }
      let targetDatas = [];
      targetDatas = resultsData[salesPivotData];
      const data = { itemNm: resultsData.itemNm, itemCd: resultsData.itemCd, total: resultsData[salesTotalData] };

      for (let i = 0, len = headers.length; i < len; i++) {
        const header = headers[i];
        data[header] = targetDatas[i];
      }
      count++;

      datas.push(data);
    }
    return datas;
  }

  const autoGroupColumnDef = {
    cellClass: 'stringType',
    editable: false,
    minWidth: 200,
    pinned: 'left',
    filterValueGetter(params) {
      const colGettingGrouped = params.colDef.showRowGroup;
      return params.api.getValue(colGettingGrouped, params.node);
    },
    cellRendererParams: {
      suppressCount: true,
      cellRenderer: null
    }
  }

  const setDatasByType = (responseData, dataType) => {
    const { pivotResultQty, pivotResultAmt } = responseData;
    const pivotResult = isEqual(dataType, AMT) ? pivotResultAmt : pivotResultQty;
    if (!isEmpty(pivotResult)) {
      const { localDateHeader, data } = pivotResult;
      currency.current = responseData?.currencyCd;
      const isAmt = isEqual(dataType, AMT);
      settingChartData(pivotResult, isAmt);
      if (!isEmpty(data)) {
        const headers = localDateHeader.map(header => headerFormatter(header));
        setGridHeaders(headers, isAmt);
        setItemNm(setGridDatas(headers, data, isAmt));
      } else {
        setGridHeaders([]);
        setItemNm([]);
      }
    } else {
      setGridHeaders([]);
      setItemNm([]);
    }
  }

  const retrieveTopItem = () => {
    onBtShowLoading();
    const params = getRequestParams();
    salesTxnService.getTopItem(params)
      .then(response => {
        if (response?.data) {
          if (!response.data?.pivotResultAmt?.data) {
            setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoData") });
          }
          setAllDatas(response.data);
          setDatasByType(response.data, toggleType);
        } else {
          setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
        debug(e);
      })
      .then(() => {
        if (reduxState) {
          const reduxStates = { ...reduxState, ...params, period: { start: params?.startDate, end: params?.endDate }, filter: filterValue, defaultCustomerHrchy, defaultValue };
          reduxDispatch(reduxUtil.updateViewState(reduxStates));
        }
        setIsLoad(false)
        onBtHide();
      })
  };

  const retrieveTopItemHrchies = () => {
    itemHrchyService.getTopHrchy({ hrchyNum: hrchyNum.HRCHY, isPermnCheck: true })
    .then(response => {
      const hrchy = response.data;
      if (hrchy !== null) {
        setDefaultValue(hrchy);
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const retrieveTopCustHrchies = () => {
    customerHrchyService.getTopHrchy({ hrchyNum: hrchyNum.HRCHY, isPermnCheck: true })
    .then(response => {
      const hrchy = response.data;
      if (hrchy !== null) {
        setDefaultCustomerHrchy(hrchy);
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const pivotConfig = {
    groupKey: "itemNm",
    chartSeries,
    include: chartLabel
  };

  const itemRangeHandler = (event) => {
    const value = event.target.value;
    if (value > 25) {
      setItemRange(25);
    } else if (value < 1) {
      setItemRange(1);
    } else {
      setItemRange(value);
    }

    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, itemRange: value }));
  }

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }

  useEffect(() => {
    if (isLoad) {
      retrieveTopItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoad]);

  useEffect(() => {
    if (defaultValue && defaultCustomerHrchy) {
      setIsLoad(true)
      // retrieveTopItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, defaultCustomerHrchy])

  useEffect(() => {
    onBtShowLoading();
    if (!reduxViewState?.filter?.customerHrchy) {
      retrieveTopCustHrchies();
    }
    if (!reduxViewState?.filter?.category) {
      retrieveTopItemHrchies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoad !== null && !isLoad && filterValue && (reduxViewState?.filter?.customerHrchy || reduxViewState?.filter.category)) {
      retrieveTopItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    if (isEqual(new ZDate(reduxViewState?.period?.start), period?.start) && isEqual(new ZDate(reduxViewState?.period?.end), period?.end) 
      && isEqual(reduxViewState?.itemRange, itemRange)) {
      return;
    }
    if (!isLoad && filterValue?.customerHrchy && filterValue?.category) {
      retrieveTopItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    if (!isEmpty(allDatas) && Object.keys(allDatas)) {
      setDatasByType(allDatas, toggleType);
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleType, itemRange]);

  useEffect(() => {
    if (columnDefs.length > 0) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs});
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  return (
    <>
      <FilterContainer>
        <Box sx={{ position: "relative", display: "flex" }}>
          <PeriodFilter period={period} setPeriod={setPeriod} />
        </Box>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultCustomerHrchy} />
          <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultValue} />
        </Box>
        <TextField
          label={`${t("itemRange")}(Max: 25)`}
          size="small"
          value={itemRange}
          name="itemRange"
          type="number"
          error={itemRange > 25}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          sx={{ textAlign: "center" }}
          onChange={itemRangeHandler}
        />
        <ToggleButton viewName={viewName} toggleType="measure" initialValue={toggleType} toggleValue={setToggleType} options={{ height: 35 }}></ToggleButton>
      </FilterContainer>
      <Box className={"grid-area"}>
        <SplitterContainer initialSizes={[1, 1]} collapseDirection='down'>
          <Box className="chart-area" style={{ height: "100%" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: FS.SUB }, isAmt: toggleType.toLowerCase() === "amt"}}
            />
          </Box>
          <Box id = "top-level-item-grid" sx={{ height: "calc(100% - 20px)", pt: 2 }}>
            <Box className="ag-theme-balham chart-grid-area" sx={{ height: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={itemNm}
                {...defaultGridMemo}
                onGridReady={onGridReady}
                autoGroupColumnDef={autoGroupColumnDef}
                groupDefaultExpanded={-1}
                groupHideOpenParents
                overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
              />
            </Box>
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
}

export default withTranslation()(DpTop25LevelItem);